import React from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { apiConfig } from "../authConfig";
import "./AuthComponents.css";

// Login Button Component
export const LoginButton = ({ className = "" }) => {
  const { instance } = useMsal();
  
  const handleLogin = async () => {
    try {
      const response = await instance.loginPopup({
        scopes: apiConfig.scopes,
      });
      // Set the active account after successful login
      if (response && response.account) {
        instance.setActiveAccount(response.account);
      }
    } catch (error) {
      console.error("Login failed:", error);
    }
  };
  
  return (
    <button 
      className={`auth-button login-button ${className}`}
      onClick={handleLogin}
    >
      Sign In
    </button>
  );
};

// Logout Button Component
export const LogoutButton = ({ className = "" }) => {
  const { instance, accounts } = useMsal();
  
  const handleLogout = async () => {
    try {
      await instance.logoutPopup({
        account: accounts[0],
      });
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };
  
  return (
    <button 
      className={`auth-button logout-button ${className}`}
      onClick={handleLogout}
    >
      Sign Out
    </button>
  );
};

// User Profile Display Component
export const UserProfile = ({ showLogout = true }) => {
  const { accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  
  if (!isAuthenticated || accounts.length === 0) {
    return null;
  }
  
  const user = accounts[0];
  const displayName = user.name || user.username;
  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
  
  return (
    <div className="user-profile">
      <div className="user-avatar">{initials}</div>
      <div className="user-info">
        <span className="user-name">{displayName}</span>
      </div>
      {showLogout && <LogoutButton />}
    </div>
  );
};

// Authentication Status Component (Login/Logout toggle)
export const AuthStatus = () => {
  const isAuthenticated = useIsAuthenticated();
  
  return (
    <div className="auth-status">
      {isAuthenticated ? <UserProfile /> : <LoginButton />}
    </div>
  );
};

// Unauthenticated Prompt Component
export const UnauthenticatedPrompt = ({ message = "Please sign in to access this content" }) => {
  return (
    <div className="unauthenticated-prompt">
      <div className="prompt-icon">🔒</div>
      <p className="prompt-message">{message}</p>
      <LoginButton />
    </div>
  );
};

// Protected Content Wrapper
export const ProtectedContent = ({ children, fallback = null }) => {
  const isAuthenticated = useIsAuthenticated();
  
  if (!isAuthenticated) {
    return fallback || <UnauthenticatedPrompt />;
  }
  
  return <>{children}</>;
};

export default {
  LoginButton,
  LogoutButton,
  UserProfile,
  AuthStatus,
  UnauthenticatedPrompt,
  ProtectedContent,
};
